import { useState } from 'react';
import { useToast } from '../../../hooks/useToast';
import { useRouter } from 'expo-router';
import { useTranslation } from 'react-i18next';
import { GraduationCap } from 'lucide-react';
import { useCVData, type CVEducation } from '../../../lib/cv-mock-data';
import { useAIDraftEducation, useAIEnhanceText } from '../../../hooks/useOnboardingApi';
import { useSparkEnhance, SparkEnhanceEntitlementError } from '../../../hooks/useSparkEnhance';
import {
  buildAiDraftProfileContext,
  mapAIDraftEducationToCVEducation,
} from '../../../lib/onboarding-ai-drafts';
import { WebInput, WebTextarea, WebSaveButton, WebFormDialog, WebItemCard, WebEmptyState, SparkButton, SparkPreview, type SparkScope } from './web-form-elements';
import { WebDatePicker } from './web-pickers';
import { DescribeToAIField } from './DescribeToAIField';
import { useHydratedArrayState } from './useHydratedFormState';
import { useEducationReviewQueue } from './useEducationReviewQueue';
import { confirmDeleteCvItem } from './delete-confirmation';
import { CvListToolbar } from './CvListToolbar';
import { ReorderableCvList } from './ReorderableCvList';
import { cvChangeNotApplied } from '../../../lib/cv-write-coordinator';

interface EditEducationFormProps {
  onSave?: () => Promise<void> | void;
  closeOnSave?: boolean;
  ctaLabel?: string;
  /** Hide skill application when no persisted CV target exists yet. */
  sparkApplySkill?: boolean;
}
function cloneEducation(item: CVEducation): CVEducation { return { ...item }; }

export function EditEducationForm({
  onSave,
  closeOnSave = true,
  ctaLabel,
  sparkApplySkill = true,
}: EditEducationFormProps) {
  const { t } = useTranslation('seeker');
  const { profile, updateProfile } = useCVData();
  const router = useRouter();
  const [isSaving, setIsSaving] = useState(false);
  const toast = useToast();
  const draftEducationMutation = useAIDraftEducation();
  const enhanceTextMutation = useAIEnhanceText();
  const spark = useSparkEnhance();

  const [items, setItems] = useHydratedArrayState(profile.education, cloneEducation);
  const [describeText, setDescribeText] = useState('');
  const [pendingDescription, setPendingDescription] = useState<string | null>(null);
  const [enhancing, setEnhancing] = useState(false);
  const review = useEducationReviewQueue({
    t,
    setItems,
    labels: { addTitle: t('cv.edit.addEducation'), editTitle: t('cv.edit.education') },
    // The Spark description preview is web-only; clear it whenever the review
    // dialog opens, advances, or closes.
    onModalReset: () => setPendingDescription(null),
  });
  const { draft, setDraft } = review;

  const enhanceDescription = async (scope: SparkScope = 'section') => {
    const text = draft.description.trim();
    if (!text || enhancing) return;
    setEnhancing(true);
    try {
      await spark.ensureAllowed();
      try {
        const result = await enhanceTextMutation.mutateAsync({
          text: draft.description,
          field: 'education_description',
          scope,
          context: { title: profile.headline },
        });
        if (result.enhanced) setPendingDescription(result.enhanced);
        else toast.error('Generation failed', 'AI returned an empty result. Please try again.');
      } finally {
        await spark.reconcile();
      }
    } catch (err) {
      if (err instanceof SparkEnhanceEntitlementError) return; // upgrade modal opened
      toast.error('Generation failed', err instanceof Error ? err.message : 'Could not enhance description');
    } finally {
      setEnhancing(false);
    }
  };
  const handleDescribe = async () => {
    const text = describeText.trim();
    if (!text || draftEducationMutation.isPending) return;
    try {
      const result = await draftEducationMutation.mutateAsync({
        prompt: text,
        profile: buildAiDraftProfileContext(profile),
      });
      const parsed = result.educations ?? [];
      if (parsed.length === 0) {
        toast.error('Generation failed', 'Could not draft education');
        return;
      }
      // A lone entry keeps the raw prompt as its description fallback; multiple
      // entries must not each inherit the whole prompt text.
      const fallback = parsed.length === 1 ? text : '';
      review.beginReview(parsed.map((e) => mapAIDraftEducationToCVEducation(e, fallback)));
      setDescribeText('');
    } catch (err) {
      toast.error('Generation failed', err instanceof Error ? err.message : 'Could not draft education');
    }
  };
  const visibleErrors = review.showErrors ? review.errors : {};

  const handleCardDelete = (i: number) => {
    void confirmDeleteCvItem(t, 'education', () => setItems(items.filter((_, idx) => idx !== i)));
  };

  const handleSave = async () => {
    if (isSaving || draftEducationMutation.isPending) return;
    updateProfile({ education: items.filter((e) => e.degree.trim() || e.school.trim()) });
    if (onSave) {
      setIsSaving(true);
      try {
        await onSave();
        toast.success(t('cv.edit.saved'));
        if (closeOnSave && router.canGoBack()) router.back();
      } catch (error) {
        // B02: a CV conflict the user answered is not a failure. "Discard"
        // keeps the version saved elsewhere; "keep editing" keeps this draft.
        const outcome = cvChangeNotApplied(error);
        if (outcome) {
          if (outcome === 'discarded' && closeOnSave && router.canGoBack()) router.back();
          return;
        }
        toast.error(t('cv.edit.saveFailed'));
      } finally {
        setIsSaving(false);
      }
    } else {
      router.back();
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <CvListToolbar
        addLabel={t('cv.edit.addEducation')}
        onAdd={review.openAdd}
        showReorderHint={items.length > 1}
      />
      <ReorderableCvList
        items={items}
        onReorder={setItems}
        keyExtractor={(item) => item.id}
        getItemLabel={(item) => item.degree || item.school || t('cv.edit.education')}
        renderItem={({ item, index }) => (
          <WebItemCard
            title={item.degree}
            subtitle={[item.school, item.year].filter(Boolean).join(' · ')}
            icon={<GraduationCap size={18} color="var(--text-secondary)" strokeWidth={1.75} />}
            onClick={() => review.openEdit(item, index)}
            onDelete={() => handleCardDelete(index)}
          />
        )}
        emptyState={
          <WebEmptyState
            icon={<GraduationCap size={32} color="var(--text-tertiary)" strokeWidth={1.25} />}
            text={t('cv.edit.noEducation')}
          />
        }
      />
      <DescribeToAIField
        value={describeText}
        onChangeText={setDescribeText}
        onSubmit={handleDescribe}
        loading={draftEducationMutation.isPending}
        disabled={isSaving}
      />
      <WebSaveButton label={ctaLabel ?? t('cv.edit.save')} onClick={handleSave} loading={isSaving} disabled={isSaving || draftEducationMutation.isPending} />

      <WebFormDialog visible={review.editingIndex !== null} title={review.modalTitle} onClose={review.close}>
        <WebInput label={t('cv.edit.degree')} value={draft.degree} onChange={(v) => setDraft({ ...draft, degree: v })} placeholder={t('cv.edit.degree')} error={visibleErrors.degree} />
        <WebInput label={t('cv.edit.school')} value={draft.school} onChange={(v) => setDraft({ ...draft, school: v })} placeholder={t('cv.edit.school')} error={visibleErrors.school} />
        <div style={{ marginTop: 12 }}>
          <WebDatePicker label={t('cv.edit.year')} value={draft.year} onSelect={(v) => setDraft({ ...draft, year: v })} placeholder={t('cv.edit.year')} />
          {visibleErrors.year ? (
            <span style={{ color: 'var(--color-danger, #dc2626)', fontSize: 12, marginTop: 4, display: 'block', paddingInline: 4 }}>{visibleErrors.year}</span>
          ) : null}
        </div>
        <WebTextarea label={t('cv.edit.description')} value={draft.description} onChange={(v) => { setDraft({ ...draft, description: v }); if (pendingDescription) setPendingDescription(null); }} placeholder={t('cv.edit.description')} rows={3} error={visibleErrors.description} />
        {draft.description.trim() ? (
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
            <SparkButton
              onEnhance={enhanceDescription}
              loading={enhancing}
              disabled={enhancing}
              showApplySkill={sparkApplySkill}
            />
          </div>
        ) : null}
        {pendingDescription ? (
          <SparkPreview
            text={pendingDescription}
            onAccept={() => { setDraft({ ...draft, description: pendingDescription }); setPendingDescription(null); }}
            onReject={() => setPendingDescription(null)}
          />
        ) : null}
        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
          <button onClick={review.commit} disabled={review.showErrors && review.hasErrors} style={{ flex: 1, height: 48, borderRadius: 9999, backgroundColor: 'var(--text-primary)', color: 'var(--bg)', fontSize: 16, fontWeight: 600, border: 'none', cursor: review.showErrors && review.hasErrors ? 'not-allowed' : 'pointer', fontFamily: 'inherit', opacity: review.showErrors && review.hasErrors ? 0.5 : 1 }}>
            {review.editingIndex === -1 ? t('cv.edit.addEducation') : t('cv.edit.save')}
          </button>
        </div>
      </WebFormDialog>
    </div>
  );
}
